const FAVORITE_TEAM_TYPES = new Set([
  'FAVORITE_TEAM_MATCH_STARTING',
  'FAVORITE_TEAM_MATCH_STARTED',
  'FAVORITE_TEAM_MATCH_FINISHED',
  'FAVORITE_TEAM_SCORED',
  'FAVORITE_TEAM_WON',
  'FAVORITE_TEAM_LOST',
  'FAVORITE_TEAM_DREW',
  'FAVORITE_TEAM_MATCH_REMINDER',
]);

const HEADLINE_KEY_BY_TYPE = {
  FAVORITE_TEAM_MATCH_STARTING: 'matchStarting',
  FAVORITE_TEAM_MATCH_STARTED: 'matchStarted',
  FAVORITE_TEAM_SCORED: 'scored',
  FAVORITE_TEAM_WON: 'won',
  FAVORITE_TEAM_LOST: 'lost',
  FAVORITE_TEAM_DREW: 'drew',
  FAVORITE_TEAM_MATCH_FINISHED: 'matchFinished',
  FAVORITE_TEAM_MATCH_REMINDER: 'matchReminder',
};

const SCORE_IN_HEADLINE_TYPES = new Set([
  'FAVORITE_TEAM_SCORED',
  'FAVORITE_TEAM_WON',
  'FAVORITE_TEAM_LOST',
  'FAVORITE_TEAM_DREW',
  'FAVORITE_TEAM_MATCH_FINISHED',
]);

function isTruthy(value) {
  return value === true || value === 'true';
}

function readData(notification) {
  const raw = notification?.data;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? raw : {};
}

export function isFavoriteTeamNotification(notification) {
  return FAVORITE_TEAM_TYPES.has(notification?.type);
}

export function isStructuredSoccerNotification(notification) {
  const data = readData(notification);
  return (
    isTruthy(data.soccerV2) ||
    isTruthy(data.worldCupV2) ||
    Boolean(data.matchId && (data.teamName || data.homeTeam || data.awayTeam))
  );
}

export function isSoccerV2FavoriteTeamNotification(notification) {
  return isFavoriteTeamNotification(notification) && isStructuredSoccerNotification(notification);
}

function scoringTeamNameFrom(data) {
  return (data.scoringTeamName || data.teamName || '').trim();
}

function buildDetail(t, data) {
  const homeTeam = (data.homeTeam || '').trim();
  const awayTeam = (data.awayTeam || '').trim();
  const score = (data.score || '').trim();
  const minute = data.minute != null && data.minute !== '' ? String(data.minute) : '';

  const parts = [];

  if (homeTeam && awayTeam) {
    parts.push(
      score
        ? t('notifications.soccerV2.scoreLine', { homeTeam, awayTeam, score })
        : t('notifications.soccerV2.versus', { homeTeam, awayTeam }),
    );
  } else if (score) {
    parts.push(t('notifications.soccerV2.currentScore', { score }));
  }

  if (minute) {
    parts.push(t('notifications.soccerV2.minute', { minute }));
  }

  return parts.join(' · ');
}

function buildHeadline(t, notification, data) {
  const type = notification?.type || '';
  const teamName =
    type === 'FAVORITE_TEAM_SCORED'
      ? scoringTeamNameFrom(data)
      : (data.teamName || '').trim();
  const score = (data.score || '').trim();
  const matchPhase = (data.matchPhase || '').trim().toLowerCase();
  const resolvedTeamName = teamName || t('notifications.soccerV2.yourTeam');
  const minutesRaw = data.minutesUntilKickoff;
  const minutes = minutesRaw != null && minutesRaw !== '' ? Number(minutesRaw) : null;

  if (matchPhase === 'halftime') {
    return t('notifications.soccerV2.halfTime', { teamName: resolvedTeamName });
  }

  const headlineKey = HEADLINE_KEY_BY_TYPE[type];
  if (!headlineKey) {
    return '';
  }

  if (SCORE_IN_HEADLINE_TYPES.has(type) && score) {
    const scoreKey = `${headlineKey}WithScore`;
    const withScore = t(`notifications.soccerV2.${scoreKey}`, {
      teamName: resolvedTeamName,
      score,
      minutes: Number.isFinite(minutes) ? Math.round(minutes) : '—',
    });
    if (withScore !== `notifications.soccerV2.${scoreKey}`) {
      return withScore;
    }
  }

  return t(`notifications.soccerV2.${headlineKey}`, {
    teamName: resolvedTeamName,
    score,
    minutes: Number.isFinite(minutes) ? Math.round(minutes) : '—',
  });
}

function buildDisplayResult(headline, detail) {
  const safeHeadline = (headline || '').trim();
  const safeDetail = (detail || '').trim();
  const combined = [safeHeadline, safeDetail].filter(Boolean).join(' — ');
  return {
    headline: safeHeadline || safeDetail,
    detail: safeHeadline && safeDetail && safeHeadline !== safeDetail ? safeDetail : '',
    combined: combined || safeHeadline || safeDetail,
  };
}

/**
 * Localized headline + match detail from notification type and structured data.
 */
export function getSoccerV2NotificationDisplay(notification, t) {
  const data = readData(notification);

  if (isTruthy(data.algeriaBroadcast) && notification?.title) {
    const headline = String(notification.title).trim();
    let detail = typeof t === 'function' ? buildDetail(t, data) : '';
    const body = (notification.body || '').trim();
    if (!detail && body && body !== headline) {
      detail = body;
    }
    return buildDisplayResult(headline, detail);
  }

  if (typeof t !== 'function') {
    const fallback = (data.teamName || notification?.title || '').trim();
    return buildDisplayResult(fallback, '');
  }

  const headline = buildHeadline(t, notification, data);
  const detail = buildDetail(t, data);

  if (!headline && notification?.title) {
    return buildDisplayResult(notification.title, detail || notification.body || '');
  }

  return buildDisplayResult(
    headline || (data.teamName || '').trim() || t('notifications.soccerV2.yourTeam'),
    detail,
  );
}

function extractMatchId(notification, data) {
  return (
    notification?.matchId ||
    notification?.match?.id ||
    data?.matchId ||
    data?.match?.id ||
    null
  );
}

function extractTeamId(notification, data) {
  return (
    notification?.teamId ||
    notification?.team?.id ||
    data?.teamId ||
    data?.team?.id ||
    notification?.match?.teamId ||
    notification?.match?.team?.id ||
    null
  );
}

/**
 * Navigation targets for favorite-team / Soccer V2 / World Cup notifications.
 */
export function resolveSoccerV2SportsRoutes(notification) {
  if (!notification) return [];

  const data = readData(notification);
  const matchId = extractMatchId(notification, data);
  const teamId = extractTeamId(notification, data);
  const worldCupV2 = isTruthy(data.worldCupV2);
  const soccerV2 = isTruthy(data.soccerV2) || worldCupV2;
  const structured = isStructuredSoccerNotification(notification);

  if (soccerV2 || structured) {
    if (matchId) {
      return [{ name: 'soccer-v2.match', params: { matchId: String(matchId) } }];
    }
    if (teamId) {
      return [{ name: 'soccer-v2.team', params: { teamId: String(teamId) } }];
    }
    if (worldCupV2) {
      if (isTruthy(data.algeriaBroadcast)) {
        return [{ name: 'soccer-v2.world-cup', query: { tab: 'algeria' } }];
      }
      return [{ name: 'soccer-v2.world-cup' }];
    }
    return [{ name: 'soccer-v2.home' }];
  }

  if (matchId) {
    return [{ name: 'app.soccer.match', params: { matchId: String(matchId) } }];
  }
  if (teamId) {
    return [{ name: 'app.soccer.team', params: { teamId: String(teamId) } }];
  }
  return [{ name: 'app.soccer' }];
}
